import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { cartService } from '../services/api'
import toast from 'react-hot-toast'
import { useNotificationStore } from './notificationStore'

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],          // { id, product_id, product, quantity, price }
      loading: false,
      count: 0,
      notifCount: 0,
      ownerId: null,      // user id this cart belongs to — cleared when a different user logs in

      // Call on login — clears cart if it belongs to a different user
      claimCart: (userId) => {
        const current = get().ownerId
        if (current !== null && current !== userId) {
          // Different user logged in — wipe local cart so their items aren't visible
          set({ items: [], count: 0, ownerId: userId })
        } else {
          set({ ownerId: userId })
        }
      },

      // Sync from API (when logged in)
      syncFromAPI: async () => {
        try {
          const data = await cartService.get()
          const rawItems = data?.items || []
          const items = rawItems.map(apiItem => {
            const prod = apiItem.product || null
            const price = apiItem.unit_price
              || apiItem.price
              || prod?.inclprice2
              || 0
            return {
              ...apiItem,
              product: prod,
              price,
            }
          })
          set({ items, count: items.reduce((sum, i) => sum + i.quantity, 0) })
        } catch { /* not logged in - keep local state */ }
      },

      addItem: async (product, quantity = 1) => {
        const items = get().items
        const existing = items.find(i => i.product_id === product.id)

        if (existing) {
          const updated = items.map(i =>
            i.product_id === product.id
              ? { ...i, quantity: i.quantity + quantity }
              : i
          )
          set({ items: updated, count: updated.reduce((s, i) => s + i.quantity, 0) })
        } else {
          const newItem = {
            id: Date.now(),
            product_id: product.id,
            product,
            quantity,
            price: product.inclprice2 || product.price || 0,
          }
          const updated = [...items, newItem]
          set({ items: updated, count: updated.reduce((s, i) => s + i.quantity, 0) })
        }

        // Try API sync
        try { await cartService.addItem(product.id, quantity) } catch { }
        toast.success(`${product.description || product.product_name || product.name || 'Product'} added to cart!`)
        set(s => ({ notifCount: s.notifCount + 1 }))
        useNotificationStore.getState().add({
          type:    'cart',
          title:   'Added to Cart',
          message: `${quantity}× ${product.product_name || product.name || 'Product'}`,
          link:    '/cart',
        })
      },

      updateQuantity: async (itemId, quantity) => {
        if (quantity < 1) { get().removeItem(itemId); return }
        const updated = get().items.map(i =>
          i.id === itemId ? { ...i, quantity } : i
        )
        set({ items: updated, count: updated.reduce((s, i) => s + i.quantity, 0) })
        try { await cartService.updateItem(itemId, quantity) } catch { }
      },

      removeItem: async (itemId) => {
        const updated = get().items.filter(i => i.id !== itemId)
        set({ items: updated, count: updated.reduce((s, i) => s + i.quantity, 0) })
        try { await cartService.removeItem(itemId) } catch { }
      },

      clearCart: async () => {
        set({ items: [], count: 0 })
        try { await cartService.clear() } catch { }
      },

      // Zero out all quantities after loading a template — user must enter their own qtys
      zeroAllQuantities: async () => {
        const zeroed = get().items.map(i => ({ ...i, quantity: 0 }))
        set({ items: zeroed, count: 0 })
        try {
          await Promise.all(zeroed.map(i => cartService.updateItem(i.id, 0)))
        } catch { }
      },

      getTotal: () => {
        return get().items.reduce((sum, item) => {
          const price = item.price
            || item.unit_price
            || item.product?.inclprice2
            || item.product?.inclprice
            || 0
          return sum + (parseFloat(price) * item.quantity)
        }, 0)
      },

      getSubtotal: () => get().getTotal(),
    }),
    {
      name: 'tonys-cart',
      partialize: (state) => ({ items: state.items, count: state.count, ownerId: state.ownerId }),
    }
  )
)
